import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import {
	AuthContextProvider,
	type AuthContextProviderQuery as authContextProviderQuery,
	authContextProviderQuery as Query,
} from '@dev-agents/auth-context';
import { envType, getEnvType, getHostname } from '@dev-agents/env';
import { ErrorBoundary } from '@dev-agents/error-boundary';
import {
	type TenantContextProviderQuery as tenantContextProviderQuery,
	tenantContextProviderQuery as TenantQuery,
} from '@dev-agents/tenant-context';
import { useEffect } from 'react';
import { useQueryLoader } from 'react-relay';

import { LoadingSkeleton } from '../common/components/skeleton/skeleton';

import { AppWithTenantContext } from './AppWithTenantContext';

const redirectUrl =
	getEnvType() === envType.PROD
		? `https://id.atlassian.com/login?continue=${encodeURIComponent(window.location.href)}`
		: `https://id.stg.internal.atlassian.com/login?continue=${encodeURIComponent(
				window.location.href,
			)}`;

const AuthErrorBoundary = ({ children }: { children: React.ReactNode }) => {
	return (
		<ErrorBoundary
			componentName="AppWithAuthContext"
			fallback={() => {
				// Redirect on auth errors except for localhost
				if (window && window.location.hostname !== 'localhost') {
					window.location.replace(redirectUrl);
				}
				return <LoadingSkeleton />;
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export const AppWithAuthAndTenantContext = () => {
	const [queryRef, loadQuery] = useQueryLoader<authContextProviderQuery>(Query);
	const [tenantQueryRef, loadTenantQuery] = useQueryLoader<tenantContextProviderQuery>(TenantQuery);
	const hostName = getHostname();

	useEffect(() => {
		loadTenantQuery({
			hostNames: [hostName],
		});
	}, [loadTenantQuery, hostName]);

	useEffect(() => {
		loadQuery({});
	}, [loadQuery]);

	return (
		<AuthErrorBoundary>
			<Placeholder name="dev-agents-auth-context">
				{queryRef ? (
					<AuthContextProvider queryRef={queryRef}>
						<AppWithTenantContext tenantQueryRef={tenantQueryRef} />
					</AuthContextProvider>
				) : (
					<UFOLoadHold name="dev-agents-auth-context" />
				)}
			</Placeholder>
		</AuthErrorBoundary>
	);
};
